// ─────────────────────────────────────────────────────────────
// The SENDER's view of a payment link. PUBLIC — no auth, no app
// account needed. Route: /pay/:token  (must be OUTSIDE auth guards
// and OUTSIDE ServiceRoute in routes.jsx).
//
// Also handles the return/success path /pay/:token/done?reference=...
// by polling verify.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiClient } from "../../api/apiServices";

export default function PublicPayPage() {
  const { token } = useParams();
  const [params]  = useSearchParams();
  const reference = params.get("reference");

  const [link, setLink]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail]     = useState("");
  const [amount, setAmount]   = useState("");
  const [submitting, setSub]  = useState(false);
  const [result, setResult]   = useState(null); // verify result

  // If returning from gateway, poll verify
  useEffect(() => {
    if (!reference) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries++;
      try {
        const r = await apiClient.get(`/mtrade/pay/verify/${reference}`);
        if (r.data.status && (r.data.data.paid || tries > 8)) {
          setResult(r.data.data);
          clearInterval(poll);
        }
      } catch { if (tries > 8) clearInterval(poll); }
    }, 1500);
    return () => clearInterval(poll);
  }, [reference]);

  // Load link details
  useEffect(() => {
    apiClient.get(`/mtrade/pay/${token}`)
      .then(r => { if (r.data.status) setLink(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const pay = async () => {
    if (!email.trim()) return;
    setSub(true);
    try {
      const r = await apiClient.post(`/mtrade/pay/${token}/initiate`, {
        email: email.trim(),
        amount: link.amount != null ? undefined : Number(amount),
      });
      if (r.data.status) {
        window.location.href = r.data.data.authorizationUrl;
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Could not start payment");
    } finally { setSub(false); }
  };

  // ── Success/return screen ───────────────────────────────────
  if (reference) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ background: "linear-gradient(160deg, #f0fdf4, #ecfdf5)" }}>
        {result?.paid ? (
          <>
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-checkbox-circle-fill text-emerald-600 text-4xl" />
            </div>
            <h1 className="text-slate-800 font-bold text-xl mb-1">Payment Successful</h1>
            <p className="text-slate-500 text-sm">₦{Number(result.amount).toLocaleString()} paid. A receipt has been emailed to you.</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4" />
            <h1 className="text-slate-800 font-bold text-lg mb-1">Confirming payment…</h1>
            <p className="text-slate-400 text-sm">This takes a few seconds.</p>
          </>
        )}
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;

  if (!link) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <i className="ri-link-unlink text-slate-300 text-4xl mb-3" />
      <p className="text-slate-600 font-semibold">Payment link not found</p>
    </div>
  );

  if (!link.payable) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <i className="ri-time-line text-amber-400 text-4xl mb-3" />
      <p className="text-slate-700 font-semibold">This link is no longer active</p>
      <p className="text-slate-400 text-sm mt-1">Please contact {link.recipient.name} for an updated link.</p>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: "linear-gradient(160deg, #f0fdf4, #ecfdf5, #f0fdfa)", fontFamily: "'Space Grotesk', sans-serif" }}>
      <div className="w-full max-w-[400px]">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Recipient */}
          <div className="px-6 pt-7 pb-5 text-center border-b border-slate-100">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 overflow-hidden">
              {link.recipient.avatar
                ? <img src={link.recipient.avatar} className="w-full h-full object-cover" alt="" />
                : <span className="text-emerald-700 font-bold text-2xl">{link.recipient.name?.charAt(0) || "?"}</span>}
            </div>
            <p className="text-slate-400 text-xs">You're paying</p>
            <p className="text-slate-800 font-bold text-lg">{link.recipient.name}</p>
            {link.title && <p className="text-slate-500 text-sm mt-1">{link.title}</p>}
            {link.description && <p className="text-slate-400 text-xs mt-1">{link.description}</p>}
          </div>

          <div className="p-6 space-y-4">
            {/* Amount */}
            {link.amount != null ? (
              <div className="text-center">
                <p className="text-slate-400 text-xs">Amount</p>
                <p className="text-slate-900 font-bold text-3xl">₦{Number(link.amount).toLocaleString()}</p>
              </div>
            ) : (
              <div>
                <label className="text-slate-600 text-sm font-semibold mb-2 block">Enter amount</label>
                <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400">
                  <span className="text-slate-400 font-bold text-lg mr-2">₦</span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
                    className="flex-1 py-3.5 outline-none text-slate-800 text-xl font-bold bg-transparent" />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="text-slate-600 text-sm font-semibold mb-2 block">Your email (for receipt)</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com"
                className="w-full bg-slate-50 border-2 border-slate-200 rounded-2xl px-4 py-3.5 text-sm outline-none focus:border-emerald-400 placeholder-slate-300" />
            </div>

            <button onClick={pay} disabled={submitting || !email.trim() || (link.amount == null && !amount)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
              {submitting ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><i className="ri-secure-payment-line" />Pay Securely</>}
            </button>
            <p className="text-center text-slate-400 text-xs">🔒 Payments are processed securely. No account needed.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
